"use client";

import { useRef, useState } from "react";
import type { ChangeEvent, FormEvent } from "react";
import Turnstile from "react-turnstile";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import {
  ERROR_TEXT_CLASS,
  FIELD_WRAPPER_CLASS,
  getInputClassesWithError,
  renderRequiredLabel,
} from "@/components/ui/formClasses";
import { validateCaptchaToken, validateEmail, validateRequired } from "@/lib/validation-helpers";

type VersaoAvaliacaoFormData = {
  nome: string;
  cargo: string;
  empresa: string;
  endereco: string;
  cidade: string;
  estado: string;
  cep: string;
  telefoneComercial: string;
  telefoneCelular: string;
  email: string;
  motivo: string;
  softwareManutencao: string;
  qualSoftware: string;
  qtdFuncionariosEmpresa: string;
  qtdFuncionariosManutencao: string;
  gerenteSupervisor: string;
};

// Render and submit the Versao de Avaliacao public form.
export function VersaoAvaliacaoForm() {
  const turnstileRef = useRef<string>("");
  const [formData, setFormData] = useState<VersaoAvaliacaoFormData>({
    nome: "",
    cargo: "",
    empresa: "",
    endereco: "",
    cidade: "",
    estado: "",
    cep: "",
    telefoneComercial: "",
    telefoneCelular: "",
    email: "",
    motivo: "",
    softwareManutencao: "",
    qualSoftware: "",
    qtdFuncionariosEmpresa: "",
    qtdFuncionariosManutencao: "",
    gerenteSupervisor: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<"idle" | "success" | "error">("idle");
  const [submitMessage, setSubmitMessage] = useState("");

  // Update field state and clear any existing field-level error.
  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // Validate all required fields before sending to the server.
  const validateForm = (): boolean => {
    const nextErrors: Record<string, string> = {};

    if (!validateRequired(formData.nome)) nextErrors.nome = "Informe o nome.";
    if (!validateRequired(formData.cargo)) nextErrors.cargo = "Informe o cargo.";
    if (!validateRequired(formData.empresa)) nextErrors.empresa = "Informe a empresa.";
    if (!validateRequired(formData.endereco)) nextErrors.endereco = "Informe o endereço.";
    if (!validateRequired(formData.cidade)) nextErrors.cidade = "Informe a cidade.";
    if (!validateRequired(formData.estado)) nextErrors.estado = "Informe o estado.";
    if (!validateRequired(formData.cep)) nextErrors.cep = "Informe o CEP.";
    if (!validateRequired(formData.telefoneComercial)) nextErrors.telefoneComercial = "Informe o telefone comercial.";

    if (!validateRequired(formData.email)) {
      nextErrors.email = "Informe o e-mail.";
    } else if (!validateEmail(formData.email)) {
      nextErrors.email = "Informe um e-mail válido.";
    }

    if (!validateRequired(formData.motivo)) nextErrors.motivo = "Informe o motivo da solicitação.";
    if (!validateRequired(formData.softwareManutencao)) nextErrors.softwareManutencao = "Informe se já utiliza software de manutenção.";
    if (!validateRequired(formData.qtdFuncionariosEmpresa)) nextErrors.qtdFuncionariosEmpresa = "Informe a quantidade total de funcionários da empresa.";
    if (!validateRequired(formData.qtdFuncionariosManutencao)) nextErrors.qtdFuncionariosManutencao = "Informe a quantidade total de funcionários da manutenção.";
    if (!validateRequired(formData.gerenteSupervisor)) nextErrors.gerenteSupervisor = "Informe o nome do gerente ou supervisor.";

    if (process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" && !validateCaptchaToken(turnstileRef.current)) {
      nextErrors.turnstile = "Confirme o CAPTCHA.";
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  // Submit the form data to the backend endpoint.
  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus("idle");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/formularios/versao-avaliacao", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          entryUrl: typeof window !== "undefined" ? window.location.href : "",
          turnstileToken: turnstileRef.current,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        setSubmitStatus("error");
        setSubmitMessage(data.message || "Não foi possível enviar o formulário.");
        setIsSubmitting(false);
        return;
      }

      setSubmitStatus("success");
      setSubmitMessage("Solicitação enviada com sucesso.");
      setFormData({
        nome: "",
        cargo: "",
        empresa: "",
        endereco: "",
        cidade: "",
        estado: "",
        cep: "",
        telefoneComercial: "",
        telefoneCelular: "",
        email: "",
        motivo: "",
        softwareManutencao: "",
        qualSoftware: "",
        qtdFuncionariosEmpresa: "",
        qtdFuncionariosManutencao: "",
        gerenteSupervisor: "",
      });
      turnstileRef.current = "";
      setIsSubmitting(false);
    } catch (error) {
      console.error("Error submitting versao-avaliacao form:", error);
      setSubmitStatus("error");
      setSubmitMessage("Erro de conexão. Tente novamente.");
      setIsSubmitting(false);
    }
  };

  const fieldWrapperClasses = FIELD_WRAPPER_CLASS;
  const errorClasses = ERROR_TEXT_CLASS;

  return (
    <section className="rounded-3xl border border-blue-900/10 bg-[linear-gradient(180deg,rgba(255,255,255,0.97),rgba(235,243,252,0.99))] p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] sm:p-8">
      <p className="text-sm font-semibold uppercase tracking-[0.22em] text-blue-700">Sistech Sistemas</p>
      <h1 className="mt-3 text-3xl font-semibold tracking-tight text-blue-950 sm:text-4xl">Solicitação de versão de avaliação do GDM</h1>

      <p className="mt-6 text-sm font-medium text-slate-700">
        <span className="text-red-600">*</span> Campos obrigatórios
      </p>

      <form className="mt-8 space-y-5" onSubmit={handleSubmit} noValidate>
        <div className={fieldWrapperClasses}>
          <label htmlFor="nome" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Nome completo")}</label>
          <input id="nome" name="nome" type="text" value={formData.nome} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.nome, isSubmitting)} />
          {errors.nome ? <p className={errorClasses}>{errors.nome}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="cargo" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Cargo")}</label>
          <input id="cargo" name="cargo" type="text" value={formData.cargo} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.cargo, isSubmitting)} />
          {errors.cargo ? <p className={errorClasses}>{errors.cargo}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="empresa" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Empresa")}</label>
          <input id="empresa" name="empresa" type="text" value={formData.empresa} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.empresa, isSubmitting)} />
          {errors.empresa ? <p className={errorClasses}>{errors.empresa}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="endereco" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Endereço")}</label>
          <input id="endereco" name="endereco" type="text" value={formData.endereco} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.endereco, isSubmitting)} />
          {errors.endereco ? <p className={errorClasses}>{errors.endereco}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="cidade" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Cidade")}</label>
          <input id="cidade" name="cidade" type="text" value={formData.cidade} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.cidade, isSubmitting)} />
          {errors.cidade ? <p className={errorClasses}>{errors.cidade}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="estado" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Estado")}</label>
          <input id="estado" name="estado" type="text" value={formData.estado} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.estado, isSubmitting)} />
          {errors.estado ? <p className={errorClasses}>{errors.estado}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="cep" className="text-sm font-medium text-blue-950">{renderRequiredLabel("CEP")}</label>
          <input id="cep" name="cep" type="text" value={formData.cep} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.cep, isSubmitting)} />
          {errors.cep ? <p className={errorClasses}>{errors.cep}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="telefoneComercial" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Tel. Comercial")}</label>
          <input id="telefoneComercial" name="telefoneComercial" type="tel" value={formData.telefoneComercial} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.telefoneComercial, isSubmitting)} />
          {errors.telefoneComercial ? <p className={errorClasses}>{errors.telefoneComercial}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="telefoneCelular" className="text-sm font-medium text-blue-950">Tel. Celular</label>
          <input id="telefoneCelular" name="telefoneCelular" type="tel" value={formData.telefoneCelular} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.telefoneCelular, isSubmitting)} />
          {errors.telefoneCelular ? <p className={errorClasses}>{errors.telefoneCelular}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="email" className="text-sm font-medium text-blue-950">{renderRequiredLabel("E-mail")}</label>
          <input id="email" name="email" type="email" value={formData.email} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.email, isSubmitting)} />
          {errors.email ? <p className={errorClasses}>{errors.email}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="motivo" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Motivo da solicitação")}</label>
          <textarea id="motivo" name="motivo" rows={4} value={formData.motivo} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.motivo, isSubmitting)} />
          {errors.motivo ? <p className={errorClasses}>{errors.motivo}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="softwareManutencao" className="text-sm font-medium text-blue-950">{renderRequiredLabel("A empresa já utiliza algum software de manutenção?")}</label>
          <input id="softwareManutencao" name="softwareManutencao" type="text" value={formData.softwareManutencao} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.softwareManutencao, isSubmitting)} />
          {errors.softwareManutencao ? <p className={errorClasses}>{errors.softwareManutencao}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="qualSoftware" className="text-sm font-medium text-blue-950">Qual?</label>
          <input id="qualSoftware" name="qualSoftware" type="text" value={formData.qualSoftware} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.qualSoftware, isSubmitting)} />
          {errors.qualSoftware ? <p className={errorClasses}>{errors.qualSoftware}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="qtdFuncionariosEmpresa" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Qtde total de funcionários da empresa")}</label>
          <input id="qtdFuncionariosEmpresa" name="qtdFuncionariosEmpresa" type="text" value={formData.qtdFuncionariosEmpresa} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.qtdFuncionariosEmpresa, isSubmitting)} />
          {errors.qtdFuncionariosEmpresa ? <p className={errorClasses}>{errors.qtdFuncionariosEmpresa}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="qtdFuncionariosManutencao" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Qtde total de funcionários da manutenção")}</label>
          <input id="qtdFuncionariosManutencao" name="qtdFuncionariosManutencao" type="text" value={formData.qtdFuncionariosManutencao} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.qtdFuncionariosManutencao, isSubmitting)} />
          {errors.qtdFuncionariosManutencao ? <p className={errorClasses}>{errors.qtdFuncionariosManutencao}</p> : null}
        </div>

        <div className={fieldWrapperClasses}>
          <label htmlFor="gerenteSupervisor" className="text-sm font-medium text-blue-950">{renderRequiredLabel("Nome do Gerente ou Supervisor da manutenção")}</label>
          <input id="gerenteSupervisor" name="gerenteSupervisor" type="text" value={formData.gerenteSupervisor} onChange={handleChange} disabled={isSubmitting} className={getInputClassesWithError(!!errors.gerenteSupervisor, isSubmitting)} />
          {errors.gerenteSupervisor ? <p className={errorClasses}>{errors.gerenteSupervisor}</p> : null}
        </div>

        {process.env.NEXT_PUBLIC_TURNSTILE_ENABLED === "true" ? (
          <div className={fieldWrapperClasses}>
            <div className={`flex justify-center p-4 ${isSubmitting ? "pointer-events-none opacity-50" : ""}`}>
              <Turnstile
                sitekey={process.env.NEXT_PUBLIC_TURNSTILE_SITE_KEY || ""}
                onSuccess={(token) => {
                  turnstileRef.current = token;

                  if (errors.turnstile) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.turnstile;
                      return next;
                    });
                  }
                }}
                onExpire={() => {
                  turnstileRef.current = "";
                }}
              />
            </div>
            {errors.turnstile ? <p className={errorClasses}>{errors.turnstile}</p> : null}
          </div>
        ) : null}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={isSubmitting}
            className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-6 py-3 text-sm font-medium text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {isSubmitting ? <LoadingSpinner size="sm" ariaLabel="Enviando formulário" /> : null}
            {isSubmitting ? "Enviando..." : "Enviar"}
          </button>
        </div>

        {submitMessage ? (
          <div
            className={`rounded-2xl border px-4 py-3 text-sm ${
              submitStatus === "success"
                ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                : "border-red-200 bg-red-50 text-red-900"
            }`}
          >
            {submitMessage}
          </div>
        ) : null}
      </form>
    </section>
  );
}
